import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Boxes, TrafficCone, Plane, Globe2 } from 'lucide-react';
import {
  getMapStyles, defaultTileStyle, STORAGE_KEY, MAPBOX_TOKEN, BASEMAP_STYLE_OPTIONS,
} from './basemapCatalog';
import useMapDetail, { MAP_DETAIL_LEVELS, isVectorBasemap } from './mapDetail';
import useMapLayerPrefs from '@/hooks/useMapLayerPrefs';
import useFeatures from '@/hooks/useFeatures';
import useWeatherPrefs from '@/hooks/useWeatherPrefs';

/** The style value MapLibre gets for a catalog entry ('default' follows the app theme). */
function entryStyleValue(entry) {
  return entry.id === 'default' ? defaultTileStyle() : entry.style;
}

/** Map detail only reshapes the two OpenFreeMap vector basemaps; URL styles count as raster. */
function supportsMapDetail(entry) {
  if (entry.id === 'default' || entry.id === 'dark') return true;
  return Boolean(entry.style) && typeof entry.style === 'object' && isVectorBasemap(entry.style);
}

function getSavedStyleId() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}

export default function MapLayersControl({ mapRef, showLayers = false, layersStatus = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const orgBasemap = useSelector((s) => s.org?.settings?.basemap) || null;
  const platformBasemaps = useSelector((s) => s.session?.platformBasemaps) || [];
  const orgDefaultId = orgBasemap?.default || 'default';
  const styles = useMemo(
    () => getMapStyles(orgBasemap, platformBasemaps),
    [orgBasemap, platformBasemaps],
  );
  const [activeId, setActiveId] = useState(() => getSavedStyleId() || orgDefaultId);
  const { layers, setLayerEnabled } = useMapLayerPrefs();
  const features = useFeatures();
  const { tileLayer, setTileLayer } = useWeatherPrefs();
  const { level: detailLevel, setLevel: setDetailLevel } = useMapDetail();

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When the app theme flips AND the user is on the theme-following "Default"
  // style, swap tile URLs to match. Explicit picks (satellite, dark) stay put.
  useEffect(() => {
    if (activeId !== 'default') return;
    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      const map = mapRef?.current?.getMap?.() || mapRef?.current;
      if (map) map.setStyle(defaultTileStyle(), BASEMAP_STYLE_OPTIONS);
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [activeId, mapRef]);

  const handleSelect = (entry) => {
    setActiveId(entry.id);
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, entry.id);
    } catch {
      // ignore
    }
    const map = mapRef?.current?.getMap?.() || mapRef?.current;
    if (map) {
      map.setStyle(entryStyleValue(entry), BASEMAP_STYLE_OPTIONS);
    }
  };

  const layerRows = [
    { key: 'cluster',   icon: Boxes,       labelKey: 'mapLayers.cluster',   visible: true },
    { key: 'traffic',   icon: TrafficCone, labelKey: 'mapLayers.traffic',   visible: features.trafficLayer && Boolean(MAPBOX_TOKEN) },
    { key: 'aircraft',  icon: Plane,       labelKey: 'mapLayers.aircraft',  visible: features.aircraftLayer },
    { key: 'liveuamap', icon: Globe2,      labelKey: 'mapLayers.liveuamap', visible: features.liveuamap,
      disabled: layersStatus ? layersStatus.liveuamap?.configured === false : false },
  ].filter((r) => r.visible);

  const activeEntry = styles.find((s) => s.id === activeId) || styles[0];
  const ActiveIcon = activeEntry.icon;
  const detailEnabled = supportsMapDetail(activeEntry);

  const handleDetail = (next) => {
    if (!detailEnabled || next === detailLevel) return;
    setDetailLevel(next);
    const map = mapRef?.current?.getMap?.() || mapRef?.current;
    if (map) map.setStyle(entryStyleValue(activeEntry), BASEMAP_STYLE_OPTIONS);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* trigger button — glass pill matching the other floating map controls */}
      <button type="button" onClick={() => setOpen(!open)} title={t('mapLayers.title', 'Map & Layers')}
        className="h-9 px-3.5 text-xs font-semibold rounded-xl flex items-center gap-2 transition-all shadow-md bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800"
        style={{ backdropFilter: 'blur(12px)' }}>
        <ActiveIcon size={15} />
      </button>

      {open && (
        <div className="absolute top-full mt-2 end-0 z-50 rounded-xl shadow-xl overflow-hidden w-[240px] p-3 space-y-3"
          style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)' }}>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              {t('mapLayers.mapType', 'Map type')}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {styles.map((entry) => {
                const Icon = entry.icon;
                const isActive = entry.id === activeId;
                return (
                  <button key={entry.id} type="button" onClick={() => handleSelect(entry)}
                    className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-[10px] transition-colors ${
                      isActive
                        ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold ring-1 ring-blue-400/50'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/60 dark:hover:bg-slate-700/40'
                    }`}>
                    <Icon size={16} />
                    <span className="truncate w-full text-center">
                      {entry.labelKey ? t(entry.labelKey, entry.fallback) : entry.fallback}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              {t('mapLayers.detail.title', 'Map detail')}
            </div>
            <div data-testid="map-detail" role="group" aria-disabled={detailEnabled ? 'false' : 'true'}
              title={detailEnabled ? t('mapLayers.detail.help', 'More brings quarter names in earlier; Less hides minor street names and places of interest') : undefined}
              className={`grid grid-cols-3 gap-1 rounded-lg p-0.5 bg-slate-100/60 dark:bg-slate-700/40 ${detailEnabled ? '' : 'opacity-40'}`}>
              {MAP_DETAIL_LEVELS.map((v) => {
                const isActive = v === detailLevel;
                return (
                  <button key={v} type="button" data-value={v} disabled={!detailEnabled}
                    aria-pressed={isActive ? 'true' : 'false'}
                    onClick={() => handleDetail(v)}
                    className={`rounded-md px-1 py-1 text-[10px] transition-colors ${
                      !detailEnabled ? 'cursor-not-allowed' : ''
                    } ${
                      isActive
                        ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-semibold shadow-sm'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-slate-800/60'
                    }`}>
                    {t(`mapLayers.detail.${v}`, { less: 'Less', normal: 'Normal', more: 'More' }[v])}
                  </button>
                );
              })}
            </div>
            {!detailEnabled && (
              <div className="mt-1 text-[10px] text-slate-400">
                {t('mapLayers.detail.vectorOnly', 'Only for the Default and Dark maps')}
              </div>
            )}
          </div>

          {showLayers && (layerRows.length > 0 || layersStatus?.weather?.available) && (
            <div>
              {layerRows.length > 0 && (
                <>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    {t('mapLayers.layers', 'Layers')}
                  </div>
                  <div className="space-y-1">
                    {layerRows.map((row) => {
                      const Icon = row.icon;
                      const on = layers[row.key];
                      return (
                        <button key={row.key} type="button" disabled={row.disabled}
                          onClick={() => setLayerEnabled(row.key, !on)}
                          title={row.disabled ? t('mapLayers.notConfigured', 'Not configured — contact your provider') : undefined}
                          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors ${
                            row.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-100/60 dark:hover:bg-slate-700/40'
                          } text-slate-700 dark:text-slate-300`}>
                          <Icon size={14} />
                          <span className="flex-1 text-start">{t(row.labelKey)}</span>
                          <span className={`h-4 w-7 rounded-full relative transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${on ? 'end-0.5' : 'start-0.5'}`} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {layersStatus?.weather?.available && (
                <>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 mt-2">
                    {t('weather.title', 'Weather')}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {['off', 'precipitation', 'clouds', 'temp', 'wind'].map((v) => (
                      <button key={v} type="button" onClick={() => setTileLayer(v)}
                        className={`px-2 py-1 rounded-lg text-[10px] transition-colors ${
                          tileLayer === v
                            ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold ring-1 ring-blue-400/50'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/60 dark:hover:bg-slate-700/40'
                        }`}>
                        {t(`weather.tile_${v}`, v)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
